import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
export function Spinner({ label = "Cargando…" }) {
    return (_jsxs("div", { className: "flex items-center gap-2 text-gray-500 text-sm", children: [_jsx("span", { className: "inline-block w-4 h-4 rounded-full border-2 border-gray-300 border-t-mezcal animate-spin" }), label] }));
}
export function Empty({ icon = "—", title, hint }) {
    return (_jsxs("div", { className: "text-center py-10 text-gray-400", children: [_jsx("div", { className: "text-4xl mb-2", children: icon }), _jsx("div", { className: "text-sm font-medium text-gray-500", children: title }), hint && _jsx("div", { className: "text-xs mt-1", children: hint })] }));
}
// Live-updating "X minutes ago" — recomputes every 15s.
export function useNow(intervalMs = 15_000) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(t);
    }, [intervalMs]);
    return now;
}
// Manager elevation: cache PIN in sessionStorage for 5 minutes
const MGR_KEY = "pos.mgr.elevated";
const MGR_TTL_MS = 5 * 60 * 1000;
export function getElevatedPin() {
    try {
        const raw = sessionStorage.getItem(MGR_KEY);
        if (!raw)
            return null;
        const { pin, ts } = JSON.parse(raw);
        if (Date.now() - ts > MGR_TTL_MS) {
            sessionStorage.removeItem(MGR_KEY);
            return null;
        }
        return pin || null;
    }
    catch {
        return null;
    }
}
export function setElevatedPin(pin) {
    try {
        sessionStorage.setItem(MGR_KEY, JSON.stringify({ pin, ts: Date.now() }));
    }
    catch { }
}
// Manager PIN prompt modal. Resolves with PIN string or null on cancel.
// If session is currently elevated AND reason isn't required, skips the modal.
export function ManagerPinModal({ title, reasonRequired = false, onResolve, }) {
    const [pin, setPin] = useState("");
    const [reason, setReason] = useState("");
    const ok = pin.length >= 4 && (!reasonRequired || reason.trim().length >= 2);
    // Auto-resolve if elevation active and no reason needed
    useEffect(() => {
        if (reasonRequired)
            return;
        const cached = getElevatedPin();
        if (cached)
            onResolve(cached, "");
    }, []); // eslint-disable-line
    return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50", onClick: () => onResolve(null, ""), children: _jsxs("div", { className: "pos-card p-5 w-full max-w-sm", onClick: (e) => e.stopPropagation(), children: [_jsx("div", { className: "text-[11px] uppercase tracking-wider text-gray-500", children: "Acci\u00F3n protegida" }), _jsx("h3", { className: "text-base font-semibold mt-0.5", children: title }), _jsxs("label", { className: "block mt-4", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wider text-gray-500", children: "PIN del manager" }), _jsx("input", { autoFocus: true, type: "password", inputMode: "numeric", value: pin, onChange: (e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6)), className: "pos-input num text-center text-2xl tracking-widest mt-1" })] }), reasonRequired && (_jsxs("label", { className: "block mt-3", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wider text-gray-500", children: "Raz\u00F3n" }), _jsx("input", { value: reason, onChange: (e) => setReason(e.target.value), maxLength: 80, placeholder: "Cliente regres\u00F3, error de barra, cortes\u00EDa\u2026", className: "pos-input mt-1" })] })), _jsxs("div", { className: "flex gap-2 mt-5", children: [_jsx("button", { onClick: () => onResolve(null, ""), className: "pos-btn pos-btn-ghost flex-1", children: "Cancelar" }), _jsx("button", { onClick: () => { if (!ok)
                                return; setElevatedPin(pin); onResolve(pin, reason.trim()); }, disabled: !ok, className: "pos-btn pos-btn-primary flex-1", children: "Confirmar" })] })] }) }));
}
// Skeleton row helper
export function SkeletonRows({ rows = 4, cols = 3 }) {
    return (_jsx(_Fragment, { children: Array.from({ length: rows }).map((_, i) => (_jsx("tr", { children: Array.from({ length: cols }).map((_, j) => (_jsx("td", { children: _jsx("div", { className: "h-3 bg-gray-100 rounded animate-pulse" }) }, j))) }, i))) }));
}
export function relativeMin(then, now) {
    return Math.max(0, Math.floor((now - then) / 60000));
}
export function ActionMenu({ children }) {
    return _jsx("div", { className: "flex gap-1.5 flex-wrap", children: children });
}
